import { useEffect, useRef, useState, type FormEvent } from "react";
import {
  api,
  ApiError,
  type BankNotification,
  type BankNotifyApp,
  type BankNotifyRule,
  type BankNotifyRulePreview,
} from "../api";
import { GlassPanel, ErrorState, EmptyState, SkeletonTable, Badge, CheckboxChip, Select, confirm, toast } from "../components";
import { useAutoRefresh } from "../hooks";
import { formatTime } from "../utils";
import {
  buildRulePatch,
  formatNotificationAmount,
  ruleReasonLabel,
  shouldHideApp,
  summarizePreview,
  RULE_SKIP_MAX,
  type BankNotifyRuleForm,
} from "../lib/bankNotifications";

const PAGE_SIZE = 50;
const PREVIEW_LIMIT = 10;

function formFor(rule?: BankNotifyRule): BankNotifyRuleForm {
  return {
    anchor: rule?.anchor ?? "",
    skip_count: rule?.skip_count ?? 0,
    require_sign: rule?.require_sign ?? true,
    speak: rule?.speak ?? true,
  };
}

function errText(e: unknown): string {
  return e instanceof ApiError ? e.message : (e as Error).message;
}

/** Chỉ đọc: hiển thị thông báo ngân hàng đã bắt được từ app trên máy POS. */
export default function BankNotificationsPage() {
  const [rows, setRows] = useState<BankNotification[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [pkgInput, setPkgInput] = useState("");
  const [pkgFilter, setPkgFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = (next: { offset?: number; pkg?: string; silent?: boolean } = {}) => {
    const nextOffset = next.offset ?? offset;
    const pkg = next.pkg ?? pkgFilter;
    if (!next.silent) {
      setLoading(true);
      setError(null);
    }
    return api.bankNotifications
      .list({ limit: PAGE_SIZE, offset: nextOffset, app_package: pkg })
      .then((r) => {
        setRows(r.notifications || []);
        setTotal(Number(r.total) || 0);
        setOffset(Number(r.offset) || 0);
      })
      .catch((e: unknown) => {
        if (next.silent) return;
        setRows([]);
        setTotal(0);
        setError(e instanceof ApiError ? e.message : (e as Error).message);
      })
      .finally(() => {
        if (!next.silent) setLoading(false);
      });
  };

  useEffect(() => { void load({ offset: 0, pkg: "" }); }, []);
  useAutoRefresh(() => { void load({ silent: true }); }, 20_000);

  const applyFilter = (e: FormEvent) => {
    e.preventDefault();
    const pkg = pkgInput.trim();
    setPkgFilter(pkg);
    if (pkg) setRulesOpen(true); // lọc app → mở cài đặt để thấy card được làm nổi bật
    void load({ offset: 0, pkg });
  };

  const goPage = (nextOffset: number) => {
    if (nextOffset < 0) return;
    void load({ offset: nextOffset });
  };

  /* ── Cài đặt đọc số tiền ─────────────────────────── */
  const [rulesOpen, setRulesOpen] = useState(false);
  const [rules, setRules] = useState<BankNotifyRule[]>([]);
  const [apps, setApps] = useState<BankNotifyApp[]>([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [rulesError, setRulesError] = useState<string | null>(null);
  const [forms, setForms] = useState<Record<string, BankNotifyRuleForm>>({});
  const [previews, setPreviews] = useState<Record<string, BankNotifyRulePreview>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const cardRefs = useRef<Record<string, HTMLElement | null>>({});

  // App rác ẩn mặc định; lựa chọn được nhớ qua localStorage (không xoá dữ liệu thật).
  const JUNK_KEY = "bankRules_showJunk";
  const [showJunk, setShowJunkState] = useState(() => localStorage.getItem(JUNK_KEY) === "1");
  const setShowJunk = (v: boolean) => {
    setShowJunkState(v);
    localStorage.setItem(JUNK_KEY, v ? "1" : "0");
  };

  const loadRules = () => {
    setRulesLoading(true);
    setRulesError(null);
    return api.bankNotifyRules
      .list()
      .then((r) => {
        const nextRules = r.rules || [];
        setRules(nextRules);
        setApps(r.apps || []);
        setForms((prev) => {
          const next: Record<string, BankNotifyRuleForm> = {};
          for (const rule of nextRules) next[rule.app_package] = formFor(rule);
          // giữ bản nháp người dùng đang gõ
          return { ...next, ...prev };
        });
      })
      .catch((e: unknown) => {
        setRules([]);
        setApps([]);
        setRulesError(errText(e));
      })
      .finally(() => setRulesLoading(false));
  };

  useEffect(() => { void loadRules(); }, []);

  // Union app có tin + app còn quy tắc (app đã ngừng gửi tin vẫn hiện để sửa/xoá).
  const packages: string[] = (() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const a of apps) if (a.app_package && !seen.has(a.app_package)) { seen.add(a.app_package); out.push(a.app_package); }
    for (const r of rules) if (r.app_package && !seen.has(r.app_package)) { seen.add(r.app_package); out.push(r.app_package); }
    return out;
  })();
  const appMeta = new Map(apps.map((a) => [a.app_package, a]));
  const ruleMeta = new Map(rules.map((r) => [r.app_package, r]));

  // App rác chưa có quy tắc lưu sẵn thì ẩn (trừ khi người dùng bấm "Hiện").
  const junkSet = new Set(packages.filter((pkg) => shouldHideApp(pkg, !!ruleMeta.get(pkg))));
  // Đang lọc đúng app rác → vẫn hiện để người dùng thấy kết quả lọc.
  const visiblePackages = showJunk
    ? packages
    : packages.filter((pkg) => !junkSet.has(pkg) || pkg === pkgFilter);

  const formOf = (pkg: string) => forms[pkg] ?? formFor(ruleMeta.get(pkg));
  const setForm = (pkg: string, patch: Partial<BankNotifyRuleForm>) =>
    setForms((prev) => ({
      ...prev,
      [pkg]: { ...(prev[pkg] ?? formFor(ruleMeta.get(pkg))), ...patch },
    }));

  // Lọc theo app đang bật → cuộn tới card đó và làm nổi bật.
  useEffect(() => {
    if (!pkgFilter) return;
    const el = cardRefs.current[pkgFilter];
    if (el) el.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [pkgFilter, packages.join("|")]);

  const preview = (pkg: string) => {
    const { anchor, skip_count, require_sign } = buildRulePatch(formOf(pkg));
    setBusy(pkg);
    return api.bankNotifyRules
      .preview({ app_package: pkg, anchor, skip_count, require_sign, limit: PREVIEW_LIMIT })
      .then((r) => setPreviews((prev) => ({ ...prev, [pkg]: r })))
      .catch((e: unknown) => toast.error(`Xem trước lỗi: ${errText(e)}`))
      .finally(() => setBusy(null));
  };

  const save = (pkg: string) => {
    const patch = buildRulePatch(formOf(pkg));
    setBusy(pkg);
    return api.bankNotifyRules
      .save(pkg, patch)
      .then(() => {
        toast.success(`Đã lưu quy tắc cho ${pkg}`);
        return loadRules();
      })
      .catch((e: unknown) => toast.error(`Lưu lỗi: ${errText(e)}`))
      .finally(() => setBusy(null));
  };

  const removeRule = async (pkg: string) => {
    const ok = await confirm({
      title: "Xoá quy tắc đọc số tiền?",
      message: `${pkg} sẽ quay lại dùng cách đọc tự động (tìm số có dấu +). Tin cũ không đổi.`,
      danger: true,
      confirmLabel: "Xoá quy tắc",
    });
    if (!ok) return;
    setBusy(pkg);
    try {
      await api.bankNotifyRules.remove(pkg);
      toast.success(`Đã xoá quy tắc của ${pkg}`);
      await loadRules();
      setForms((prev) => ({ ...prev, [pkg]: formFor(undefined) }));
      setPreviews((prev) => { const n = { ...prev }; delete n[pkg]; return n; });
    } catch (e: unknown) {
      toast.error(`Xoá lỗi: ${errText(e)}`);
    } finally {
      setBusy(null);
    }
  };

  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const hasPrev = offset > 0;
  // total có thể vắng (contract optional) → suy ra từ trang đầy.
  // Có total thật: "Sau" chỉ bật khi còn bản ghi sau trang này (không còn trang chết).
  const hasNext = total > 0 ? offset + PAGE_SIZE < total : rows.length === PAGE_SIZE;
  const totalPages = total > 0
    ? Math.max(1, Math.ceil(total / PAGE_SIZE))
    : currentPage + (hasNext ? 1 : 0);

  return (
    <GlassPanel title="Thông báo ngân hàng" badge={`${total} thông báo`} badgeVariant="info">
      <section style={{ marginBottom: 16 }} aria-labelledby="bank-rules-heading">
        <button
          type="button"
          className="btn btn-ghost btn-compact"
          aria-expanded={rulesOpen}
          aria-controls="bank-rules-body"
          onClick={() => setRulesOpen((v) => !v)}
          style={{ display: "flex", alignItems: "center", gap: 6 }}
        >
          <span className="material-symbols-outlined" aria-hidden="true">
            {rulesOpen ? "expand_more" : "chevron_right"}
          </span>
          <span id="bank-rules-heading">Cài đặt đọc số tiền</span>
          <Badge variant="ghost">{rules.length} quy tắc</Badge>
        </button>

        {rulesOpen && (
          <div id="bank-rules-body" style={{ marginTop: 10 }}>
            <p style={{ margin: "0 0 10px", fontSize: 12, color: "var(--text-secondary)" }}>
              Quy tắc chỉ áp dụng cho tin mới. Tin đã bắt không được đọc lại.
              Để trống mốc chữ = tự động tìm số có dấu +.
            </p>

            {rulesError ? (
              <ErrorState error={rulesError} onRetry={() => void loadRules()} />
            ) : rulesLoading && packages.length === 0 ? (
              <SkeletonTable rows={2} cols={4} />
            ) : packages.length === 0 ? (
              <EmptyState
                icon="rule"
                title="Chưa phát hiện app ngân hàng"
                description="App sẽ xuất hiện ở đây sau khi máy POS bắt được thông báo đầu tiên."
              />
            ) : (
              <>
                {junkSet.size > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 8,
                      alignItems: "center",
                      marginBottom: 10,
                      fontSize: 12,
                      color: "var(--text-dim)",
                    }}
                  >
                    <span>
                      {showJunk
                        ? `Đang hiện ${junkSet.size} app rác (test/ô trống)`
                        : `Đã ẩn ${junkSet.size} app rác (test/ô trống)`}
                    </span>
                    <button
                      type="button"
                      className="btn btn-ghost btn-compact"
                      aria-expanded={showJunk}
                      onClick={() => setShowJunk(!showJunk)}
                    >
                      {showJunk ? "Ẩn" : "Hiện"}
                    </button>
                  </div>
                )}
                {visiblePackages.length === 0 ? (
                  <EmptyState
                    icon="rule"
                    title="Không còn app nào để cấu hình"
                    description="Toàn bộ app hiện có bị coi là rác. Bấm Hiện để xem hoặc xoá tin rác trên máy POS."
                  />
                ) : null}
                {visiblePackages.map((pkg) => {
                const form = formOf(pkg);
                const rule = ruleMeta.get(pkg);
                const meta = appMeta.get(pkg);
                const pv = previews[pkg];
                const sum = pv ? summarizePreview(pv.samples) : null;
                const broken = !!sum && sum.nulls > 0;
                const active = pkgFilter === pkg;
                return (
                  <article
                    key={pkg}
                    ref={(el) => { cardRefs.current[pkg] = el; }}
                    aria-label={`Quy tắc đọc số tiền cho ${pkg}`}
                    style={{
                      padding: 12,
                      marginBottom: 10,
                      borderRadius: 12,
                      border: `1px solid ${active ? "var(--accent-blue, #60a5fa)" : "var(--border-base)"}`,
                      background: active ? "var(--accent-blue-bg, rgba(96,165,250,.08))" : "var(--bg-card)",
                      boxShadow: active ? "0 0 0 2px var(--accent-blue-muted, rgba(96,165,250,.25))" : undefined,
                    }}
                  >
                    <header style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 8 }}>
                      <Badge variant="info">{pkg}</Badge>
                      {junkSet.has(pkg) && <Badge variant="ghost">rác</Badge>}
                      <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                        {meta ? `${meta.count} tin` : "không còn tin"}
                        {meta?.last_seen_at ? ` · gần nhất ${formatTime(meta.last_seen_at)}` : ""}
                      </span>
                      {rule ? (
                        <Badge variant="ok">đã lưu {rule.updated_at ? formatTime(rule.updated_at) : ""}</Badge>
                      ) : (
                        <Badge variant="ghost">chưa có quy tắc</Badge>
                      )}
                    </header>

                    {junkSet.has(pkg) && (
                      <p
                        role="note"
                        style={{
                          margin: "0 0 8px",
                          fontSize: 12,
                          color: "var(--status-amber)",
                        }}
                      >
                        MacroDroid đang gửi biến chưa điền (hoặc app tự test khi deploy). Sửa macro trên máy POS để
                        hết sinh app này.
                      </p>
                    )}

                    <div className="bank-rule-fields">
                      <div className="form-group">
                        <label htmlFor={`anchor-${pkg}`}>Mốc chữ (anchor)</label>
                        <input
                          id={`anchor-${pkg}`}
                          type="text"
                          value={form.anchor}
                          placeholder="Số dư TK VCB"
                          autoComplete="off"
                          onChange={(e) => setForm(pkg, { anchor: e.target.value })}
                        />
                      </div>

                      <div className="form-group">
                        {/* Select là <button aria-label>, nên nhãn hiển thị dùng span (label htmlFor chỉ
                            gắn được với form control thật). */}
                        <span
                          style={{ fontSize: 12, fontWeight: 500, color: "var(--text-secondary)" }}
                        >
                          Bỏ qua số đầu
                        </span>
                        <Select
                          id={`skip-${pkg}`}
                          value={String(form.skip_count)}
                          options={Array.from({ length: RULE_SKIP_MAX + 1 }, (_, i) => ({
                            value: String(i),
                            label: String(i),
                          }))}
                          onChange={(v) => setForm(pkg, { skip_count: Number(v) })}
                          ariaLabel={`Số ứng viên bỏ qua cho ${pkg}`}
                        />
                      </div>

                      <div className="bank-rule-fields__chips">
                        <CheckboxChip
                          checked={form.require_sign}
                          onChange={(v) => setForm(pkg, { require_sign: v })}
                          label="Chỉ nhận số có dấu +"
                        />
                        <CheckboxChip
                          checked={form.speak}
                          onChange={(v) => setForm(pkg, { speak: v })}
                          label="Đọc loa"
                        />
                      </div>
                    </div>
                    <span className="form-hint" style={{ display: "block", marginTop: 6 }}>
                      Mốc chữ để trống = tự động tìm số có dấu +
                    </span>

                    <div className="action-buttons" style={{ marginTop: 10 }}>
                      <button
                        type="button"
                        className="btn btn-secondary btn-compact"
                        disabled={busy === pkg}
                        onClick={() => void preview(pkg)}
                      >
                        Xem trước
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary btn-compact"
                        disabled={busy === pkg}
                        onClick={() => void save(pkg)}
                      >
                        Lưu
                      </button>
                      {rule && (
                        <button
                          type="button"
                          className="btn btn-ghost btn-compact"
                          disabled={busy === pkg}
                          onClick={() => void removeRule(pkg)}
                        >
                          Xoá quy tắc
                        </button>
                      )}
                    </div>

                    {pv && sum && (
                      <div style={{ marginTop: 10 }}>
                        <div
                          role="status"
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 8,
                            alignItems: "center",
                            fontSize: 13,
                            fontWeight: 600,
                            color: broken ? "var(--status-amber)" : "var(--status-green)",
                          }}
                        >
                          <span>{`Đọc được ${sum.ok}/${pv.samples.length} tin gần nhất`}</span>
                          {broken && (
                            <Badge variant="warn">{`${sum.nulls} tin không đọc được — kiểm tra mốc chữ`}</Badge>
                          )}
                        </div>
                        <div className="table-wrap" style={{ marginTop: 6, overflow: "auto" }}>
                          <table className="variant-table" style={{ width: "100%", minWidth: 480 }}>
                            <thead>
                              <tr>
                                <th style={{ whiteSpace: "nowrap" }}>Thời gian</th>
                                <th>Số khớp</th>
                                <th style={{ textAlign: "right", whiteSpace: "nowrap" }}>Số tiền</th>
                                <th>Kết quả</th>
                              </tr>
                            </thead>
                            <tbody>
                              {pv.samples.map((s) => (
                                <tr key={s.id}>
                                  <td style={{ whiteSpace: "nowrap" }}>
                                    {s.created_at ? formatTime(s.created_at) : "—"}
                                  </td>
                                  <td style={{ maxWidth: 240 }} title={s.body || undefined}>
                                    {s.matched_text ? (
                                      <mark style={{ background: "var(--accent-blue-muted, rgba(96,165,250,.3))", color: "inherit", padding: "0 2px", borderRadius: 3 }}>
                                        {s.matched_text}
                                      </mark>
                                    ) : (
                                      <span style={{ color: "var(--text-dim)" }}>—</span>
                                    )}
                                  </td>
                                  <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                                    {formatNotificationAmount(s.amount_vnd)}
                                  </td>
                                  <td style={{ whiteSpace: "nowrap" }}>
                                    <Badge variant={s.reason === "OK" ? "ok" : s.amount_vnd == null ? "warn" : "ghost"}>
                                      {ruleReasonLabel(s.reason)}
                                    </Badge>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </article>
                );
              })}
              </>
            )}
          </div>
        )}
      </section>

      <form className="filter-toolbar" onSubmit={applyFilter} role="search">
        <div className="filter-row-search">
          <input
            type="search"
            value={pkgInput}
            onChange={(e) => setPkgInput(e.target.value)}
            placeholder="Lọc theo app_package, vd: com.vietcombank.mobile"
            aria-label="Lọc theo app_package"
          />
        </div>
        <div className="action-buttons">
          <button type="submit" className="btn btn-ghost" disabled={loading}>Lọc</button>
          {pkgFilter && (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => { setPkgInput(""); setPkgFilter(""); void load({ offset: 0, pkg: "" }); }}
            >
              Bỏ lọc
            </button>
          )}
        </div>
      </form>

      {error ? (
        <ErrorState error={error} onRetry={() => void load()} />
      ) : loading ? (
        <SkeletonTable rows={5} cols={5} />
      ) : rows.length === 0 ? (
        <EmptyState
          icon="notifications_off"
          title="Chưa có thông báo nào"
          description={pkgFilter ? `Không có thông báo từ "${pkgFilter}".` : "Thông báo ngân hàng sẽ xuất hiện khi app trên máy POS bắt được."}
        />
      ) : (
        <>
          <div className="table-wrap" style={{ overflow: "auto", maxHeight: "calc(100vh - 280px)", borderRadius: 12 }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ whiteSpace: "nowrap" }}>Thời gian</th>
                  <th style={{ textAlign: "right", whiteSpace: "nowrap" }}>Số tiền</th>
                  <th>App</th>
                  <th>Tiêu đề</th>
                  <th>Nội dung</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((n) => (
                  <tr key={n.id}>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {n.received_at ? formatTime(n.received_at) : n.created_at ? formatTime(n.created_at) : "—"}
                    </td>
                    <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                      {formatNotificationAmount(n.amount_vnd)}
                    </td>
                    <td><Badge variant="info">{n.app_package}</Badge></td>
                    <td style={{ maxWidth: 220 }} title={n.title || undefined}>
                      <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {n.title || "—"}
                      </div>
                    </td>
                    <td style={{ maxWidth: 420 }} title={n.body || undefined}>
                      <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {n.body || "—"}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="data-table-pagination">
            <div className="page-info">
              Trang {currentPage} / {totalPages} · {total} thông báo
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <button disabled={loading || !hasPrev} onClick={() => goPage(offset - PAGE_SIZE)}>Trước</button>
              <button disabled={loading || !hasNext} onClick={() => goPage(offset + PAGE_SIZE)}>Sau</button>
            </div>
          </div>
        </>
      )}
    </GlassPanel>
  );
}
